<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Contratos de alquiler</h1>
    </div>

    <!-- Leyendas -->
    <div class="legend mb-3">
      <span class="legend-title">Pago:</span>
      <span class="legend-item"><span class="badge badge-green">Al día</span></span>
      <span class="legend-item"><span class="badge badge-yellow">Pendiente</span></span>
      <span class="legend-item"><span class="badge badge-danger">Vencido</span></span>
      <span class="legend-sep">|</span>
      <span class="legend-title">Contrato:</span>
      <span class="legend-item"><span class="dot green"></span> &gt;30 días</span>
      <span class="legend-item"><span class="dot yellow"></span> ≤30 días</span>
      <span class="legend-item"><span class="dot red"></span> Vencido</span>
    </div>

    <div class="table-container">
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Vehículo</th>
              <th>Propietario</th>
              <th>Alquiler</th>
              <th>Día pago</th>
              <th>Estado de pago</th>
              <th>Último pago</th>
              <th>Contrato vence</th>
              <th>Contrato</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.vehicle_id">
              <td class="text-center pl-3 font-weight-bold">{{ r.vehicle_plate }}</td>
              <td class="text-center">{{ r.owner_name || '—' }}</td>
              <td class="text-center">{{ r.rent_amount != null ? formatMoney(r.rent_amount, r.rent_currency) : '—' }}</td>
              <td class="text-center">{{ r.rent_payment_day != null ? r.rent_payment_day : '—' }}</td>
              <td class="text-center">
                <span :class="`badge ${paymentMeta(r.payment_status).color}`">{{ paymentMeta(r.payment_status).label }}</span>
              </td>
              <td class="text-center">{{ r.last_payment_date ? formatDate(r.last_payment_date) : '—' }}</td>
              <td class="text-center">
                <template v-if="r.contract_end_date">
                  <span :class="contractText(r.contract_alert)">{{ formatDate(r.contract_end_date) }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <button v-if="r.has_contract" type="button" class="link-button" title="Ver contrato"
                  @click="viewContract(r)"><i class="ri-file-text-line"></i></button>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Registrar pago"
                      @click="$router.push({ name: 'fleet-rental-payment-create', params: { vehicleId: r.vehicle_id } })" />
                    <DropdownItem text="Ver pagos"
                      @click="$router.push({ name: 'fleet-rental-payments', params: { vehicleId: r.vehicle_id } })" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-center text-muted py-4">No hay vehículos alquilados en esta ciudad.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert, DropdownTable, DropdownItem } from 'wize-admin'
import { FleetRentalService } from '@/core/services'

const PAYMENT = {
  paid: { label: 'Al día', color: 'badge-green' },
  pending: { label: 'Pendiente', color: 'badge-yellow' },
  overdue: { label: 'Vencido', color: 'badge-danger' },
  no_config: { label: 'Sin día de pago', color: 'badge-secondary' }
}

export default {
  name: 'RentalStatus',
  components: { DropdownTable, DropdownItem },
  data() {
    return { rows: [] }
  },
  methods: {
    paymentMeta(s) { return PAYMENT[s] || { label: s, color: 'badge-secondary' } },
    contractText(alert) {
      if (alert === 'red') return 'text-danger font-weight-bold'
      if (alert === 'yellow') return 'text-warning font-weight-bold'
      return ''
    },
    formatMoney(v, currency) {
      const cur = currency || 'PEN'
      const sym = cur === 'PEN' ? 'S/' : cur
      return `${sym} ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    async viewContract(r) {
      try {
        const res = await FleetRentalService.getContract(r.vehicle_id)
        const url = res.payload ? res.payload.url : null
        if (url) window.open(url, '_blank')
        else Alert.info('No hay contrato disponible.')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo abrir el contrato.')
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetRentalService.status()
        this.rows = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener el estado de alquileres')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.legend
  display flex
  flex-wrap wrap
  gap 12px
  align-items center
  font-size 12px
  color #6c757d
.legend-title
  font-weight 600
.legend-item
  display flex
  align-items center
  gap 6px
.legend-sep
  color #ced4da
.dot
  width 10px
  height 10px
  border-radius 50%
  display inline-block
  &.green
    background #25C26E
  &.yellow
    background #f59e0b
  &.red
    background #dc3545
.link-button
  background none
  border none
  color #007bff
  font-size 16px
  cursor pointer
  &:hover
    color #0056b3
</style>
